import React, { useState, useCallback } from 'react';
import { View, Text, Pressable, LayoutAnimation, UIManager, Platform } from 'react-native';
import { ChevronDown, ChevronUp, AlertTriangle, Flag, TrendingUp, Lightbulb, ShieldAlert } from 'lucide-react-native';
import { cn } from '@/lib/cn';
import { useThemeColors } from '@/lib/useThemeColors';
import {
  labelThemeColor,
  labelThemeBgColor,
  labelThemeBorderColor,
  evidenceLevelToLabel,
  evidenceLevelToSource,
  evidenceSourceColor,
  evidenceThemeColor,
  riskThemeColor,
  riskLevelToDisplay,
  capitalizeLabel,
  scoreToLabel,
} from '@/lib/scoring/score-display';
import { formatClaimsSummary, subScoreEvidenceLine, type ClaimsSummary } from '@/lib/scoring/backing-status';

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

/** Per-sub-criterion score + evidence (v2.1.0+). Server omits weights (trade secret). */
export interface SubScoreView {
  id: string;
  score: number;
  rationale?: string;
  /** Verbatim quotes + source: founders, admins, investors WITH a data-room grant. */
  evidence?: Array<{ quote: string; source: string }>;
  /** Investors WITHOUT a data-room grant: evidence as backing status instead. */
  evidenceSummary?: { citations: number };
  claimsSummary?: ClaimsSummary;
}

interface DimensionCardProps {
  name: string;
  rawScore: number;
  feedback: string;
  summary?: string;
  evidenceLevel: string;
  /** Per-sub-criterion breakdown (v2.1.0+). When present, renders an audit-trail panel. */
  subScores?: SubScoreView[];
  /** Investor without a data-room grant: claim-status counts for the dimension. */
  claimsSummary?: ClaimsSummary;
  /** Per-dimension confidence 0-100 (preferred over evidenceLevel for badge) */
  confidence?: number;
  riskLevel: string;
  /** Optional per-dimension risk count breakdown (high/medium/low). When supplied,
   *  the risk pill renders multi-segment with per-severity colors. */
  riskCounts?: { high: number; medium: number; low: number };
  dimensionRisk?: { description: string; severity: string; mitigation?: string };
  role: 'startup' | 'investor';
  improvementTip?: string;
  hasDDFlag?: boolean;
  ddFlagText?: string;
  verificationClaims?: number;
  verificationVerified?: number;
  verificationSelfReported?: number;
  verificationContradictions?: number;
  evidenceDetail?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function RiskCard({
  dimensionRisk,
  colors,
}: {
  dimensionRisk: { description: string; severity: string; mitigation?: string };
  colors: any;
}) {
  const isHigh = dimensionRisk.severity === 'high';
  const bgColor = isHigh ? colors.red.light : colors.amber.light;
  const textColor = isHigh ? colors.error : colors.warning;
  const Icon = isHigh ? ShieldAlert : AlertTriangle;

  return (
    <View className="rounded-xl p-3 mt-3" style={{ backgroundColor: bgColor }}>
      <View className="flex-row items-start gap-2">
        <Icon size={14} color={textColor} style={{ marginTop: 1 }} />
        <View className="flex-1">
          <Text
            style={{
              color: textColor,
              fontSize: 9,
              fontWeight: '700',
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            {capitalizeLabel(dimensionRisk.severity)} Risk
          </Text>
          <Text className="text-xs leading-4" style={{ color: textColor, opacity: 0.85 }} numberOfLines={5}>
            {dimensionRisk.description}
          </Text>
          {dimensionRisk.mitigation ? (
            <Text className="text-xs leading-4 mt-1.5 italic" style={{ color: textColor, opacity: 0.7 }} numberOfLines={4}>
              Mitigation: {dimensionRisk.mitigation}
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  );
}

function ImprovementTipCard({ tip, colors }: { tip: string; colors: any }) {
  return (
    <View className="rounded-xl p-3 mt-3" style={{ backgroundColor: colors.primary[50] }}>
      <View className="flex-row items-start gap-2">
        <Lightbulb size={14} color={colors.info} style={{ marginTop: 1 }} />
        <View className="flex-1">
          <Text
            style={{
              color: colors.info,
              fontSize: 9,
              fontWeight: '700',
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            Improve This Score
          </Text>
          <Text className="text-xs leading-4" style={{ color: colors.text.secondary }} numberOfLines={6}>
            {tip}
          </Text>
        </View>
      </View>
    </View>
  );
}

function VerificationTable({
  claims,
  verified,
  selfReported,
  contradictions,
  colors,
}: {
  claims?: number;
  verified?: number;
  selfReported?: number;
  contradictions?: number;
  colors: any;
}) {
  const rows: { label: string; value: number | undefined; color?: string }[] = [
    { label: 'Total Claims', value: claims },
    { label: 'Verified', value: verified, color: colors.success },
    { label: 'Self-Reported', value: selfReported, color: colors.warning },
    { label: 'Contradictions', value: contradictions, color: colors.error },
  ];

  return (
    <View className="rounded-xl overflow-hidden mt-3" style={{ backgroundColor: colors.surface.secondary }}>
      <Text
        style={{
          color: colors.text.tertiary,
          fontSize: 9,
          fontWeight: '700',
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          paddingHorizontal: 12,
          paddingTop: 12,
          paddingBottom: 8,
        }}
      >
        Verification Status
      </Text>
      {rows.map((row, i) => (
        <View
          key={`vrow-${i}`}
          className={cn('flex-row items-center justify-between px-3 py-2', {
            'border-t': i > 0,
          })}
          style={{ borderColor: colors.border.light }}
        >
          <Text className="text-xs" style={{ color: colors.text.secondary }}>
            {row.label}
          </Text>
          <Text
            className="text-xs font-semibold"
            style={{ color: row.color ?? colors.text.primary }}
          >
            {row.value ?? '—'}
          </Text>
        </View>
      ))}
    </View>
  );
}

/** Turn a kebab-case sub-criterion id into a Title Case label. */
function humanizeSubId(id: string): string {
  return id
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Color for a 1-10 sub-score, reusing the score-label palette. */
function subScoreColor(score: number, colors: any): string {
  // 1-10 → 0-100 to reuse scoreToLabel bands.
  return labelThemeColor(scoreToLabel(score * 10), colors);
}

function SubCriteriaPanel({
  subScores,
  colors,
}: {
  subScores: SubScoreView[];
  colors: any;
}) {
  return (
    <View className="mt-3">
      <Text
        style={{
          color: colors.text.tertiary,
          fontSize: 9,
          fontWeight: '700',
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        Sub-Criteria
      </Text>
      <View className="gap-2">
        {subScores.map((sub, i) => {
          const sc = subScoreColor(sub.score, colors);
          const evidenceLine = subScoreEvidenceLine(sub);
          const claimsLine = formatClaimsSummary(sub.claimsSummary);
          return (
            <View
              key={`sub-${sub.id}-${i}`}
              className="rounded-xl p-3"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              {/* Header: name + score/10 */}
              <View className="flex-row items-center justify-between">
                <Text
                  className="text-xs font-semibold flex-1"
                  style={{ color: colors.text.primary }}
                  numberOfLines={1}
                >
                  {humanizeSubId(sub.id)}
                </Text>
                <View
                  className="px-2 py-0.5 rounded-md ml-2"
                  style={{ backgroundColor: `${sc}18` }}
                >
                  <Text className="text-[10px] font-bold" style={{ color: sc }}>
                    {sub.score}/10
                  </Text>
                </View>
              </View>

              {/* Rationale */}
              {sub.rationale ? (
                <Text
                  className="text-xs leading-4 mt-1"
                  style={{ color: colors.text.secondary }}
                  numberOfLines={3}
                >
                  {sub.rationale}
                </Text>
              ) : null}

              {/* Evidence: quotes, or — without a data-room grant — backing status */}
              {evidenceLine.kind === 'quotes' ? (
                <View className="mt-2 gap-1.5">
                  {(sub.evidence ?? []).map((ev, ei) => (
                    <View
                      key={`ev-${sub.id}-${ei}`}
                      className="rounded-lg px-2.5 py-2"
                      style={{
                        backgroundColor: colors.surface.tertiary,
                        borderLeftWidth: 2,
                        borderLeftColor: colors.primary[400],
                      }}
                    >
                      <Text
                        className="text-xs leading-4 italic"
                        style={{ color: colors.text.secondary }}
                        numberOfLines={4}
                      >
                        “{ev.quote}”
                      </Text>
                      {ev.source ? (
                        <Text
                          className="text-[10px] mt-1"
                          style={{ color: colors.text.tertiary }}
                          numberOfLines={1}
                        >
                          — {ev.source}
                        </Text>
                      ) : null}
                    </View>
                  ))}
                </View>
              ) : evidenceLine.kind === 'citations' ? (
                <Text
                  className="text-[10px] mt-2"
                  style={{ color: colors.text.tertiary }}
                  numberOfLines={1}
                >
                  {evidenceLine.text}
                </Text>
              ) : (
                <Text
                  className="text-[10px] mt-2"
                  style={{ color: colors.text.tertiary }}
                  numberOfLines={1}
                >
                  No evidence in input — gap flagged
                </Text>
              )}
              {claimsLine ? (
                <Text
                  className="text-[10px] mt-1"
                  style={{ color: colors.text.tertiary }}
                  numberOfLines={1}
                >
                  {claimsLine}
                </Text>
              ) : null}
            </View>
          );
        })}
      </View>
    </View>
  );
}

function DDFlagCard({ text, colors }: { text?: string; colors: any }) {
  return (
    <View className="rounded-xl p-3 mt-3" style={{ backgroundColor: colors.red.light }}>
      <View className="flex-row items-start gap-2">
        <Flag size={14} color={colors.error} style={{ marginTop: 1 }} />
        <View className="flex-1">
          <Text
            style={{
              color: colors.error,
              fontSize: 9,
              fontWeight: '700',
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            Due Diligence Flag
          </Text>
          {text ? (
            <Text className="text-xs leading-4" style={{ color: colors.error, opacity: 0.85 }} numberOfLines={6}>
              {text}
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export function DimensionCard({
  name,
  rawScore,
  feedback,
  summary,
  evidenceLevel,
  subScores,
  claimsSummary,
  confidence,
  riskLevel,
  riskCounts,
  dimensionRisk,
  role,
  improvementTip,
  hasDDFlag,
  ddFlagText,
  verificationClaims,
  verificationVerified,
  verificationSelfReported,
  verificationContradictions,
  evidenceDetail,
}: DimensionCardProps) {
  const colors = useThemeColors();
  const [isOpen, setIsOpen] = useState(false);

  const toggle = useCallback(() => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setIsOpen((prev) => !prev);
  }, []);

  // Derived values
  const label = scoreToLabel(rawScore);
  const scoreColor = labelThemeColor(label, colors);
  const cardBg = label === 'weak' || label === 'critical'
    ? labelThemeBgColor(label, colors)
    : colors.surface.primary;
  const cardBorder = labelThemeBorderColor(label, colors);

  const displaySummary = summary ?? (feedback.length > 80 ? feedback.slice(0, 80) + '…' : feedback);

  const evidLabel = evidenceLevelToLabel(evidenceLevel);
  const evidenceColor = evidenceThemeColor(evidLabel, colors);
  const evidSource = evidenceLevelToSource(evidenceLevel, !!feedback);
  const evidSourceColor = evidenceSourceColor(evidSource, colors);
  // Fallback confidence when not supplied — derive from evidence level.
  // Replace once LLM emits per-dim confidence directly (Phase 102).
  const effectiveConfidence =
    confidence ??
    (evidenceLevel === 'verified'
      ? 85
      : evidenceLevel === 'partially-verified'
        ? 65
        : evidenceLevel === 'unverified'
          ? 40
          : 25);
  const riskDisplay = riskLevelToDisplay(riskLevel);
  const riskColor = riskThemeColor(riskLevel, colors);

  return (
    <View
      className="rounded-2xl overflow-hidden mb-3"
      style={{
        backgroundColor: cardBg,
        borderWidth: 1,
        borderColor: cardBorder,
      }}
    >
      {/* ── Tappable Header ── */}
      <View style={{ overflow: 'hidden', borderRadius: 16 }}>
        <Pressable
          onPress={toggle}
          accessibilityRole="button"
          accessibilityLabel={`${name}, score ${rawScore}. ${isOpen ? 'Collapse' : 'Expand'} details`}
          style={{ paddingHorizontal: 14, paddingVertical: 12 }}
        >
          {/* Row 1: Name + DD Flag + Chevron | Score Label */}
          <View className="flex-row items-center">
            <View className="flex-row items-center gap-1.5 flex-1">
              <Text
                className="text-sm font-semibold"
                style={{ color: colors.text.primary }}
                numberOfLines={1}
              >
                {name}
              </Text>
              {hasDDFlag && role === 'investor' ? (
                <Flag size={12} color={colors.error} />
              ) : null}
              {isOpen ? (
                <ChevronUp size={14} color={colors.text.tertiary} />
              ) : (
                <ChevronDown size={14} color={colors.text.tertiary} />
              )}
            </View>
            {/* Score badge */}
            <View
              className="flex-row items-center gap-1 px-2.5 py-1 rounded-lg ml-3"
              style={{ backgroundColor: `${scoreColor}18` }}
            >
              <TrendingUp size={11} color={scoreColor} />
              <Text className="text-xs font-bold" style={{ color: scoreColor }}>
                {capitalizeLabel(label)} ({rawScore})
              </Text>
            </View>
          </View>

          {/* Row 2: One-liner summary */}
          <Text
            className="text-xs mt-1.5 leading-4"
            style={{ color: colors.text.secondary }}
            numberOfLines={2}
          >
            {displaySummary}
          </Text>

          {/* Row 3: Source + Confidence + Risk pills */}
          <View className="flex-row items-center gap-3 mt-2 flex-wrap">
            <View className="flex-row items-center gap-1">
              <Text className="text-[10px]" style={{ color: colors.text.tertiary }}>
                Source:
              </Text>
              <Text className="text-[10px] font-semibold" style={{ color: evidSourceColor }}>
                {evidSource}
              </Text>
            </View>
            <Text style={{ color: colors.border.light }}>·</Text>
            <View className="flex-row items-center gap-1">
              <Text className="text-[10px]" style={{ color: colors.text.tertiary }}>
                Confidence:
              </Text>
              <Text className="text-[10px] font-semibold" style={{ color: colors.text.primary }}>
                {Math.round(effectiveConfidence)}%
              </Text>
            </View>
            <Text style={{ color: colors.border.light }}>·</Text>
            <View className="flex-row items-center gap-1 flex-wrap">
              <Text className="text-[10px]" style={{ color: colors.text.tertiary }}>
                Risk:
              </Text>
              {(() => {
                const totalRiskCount = riskCounts
                  ? riskCounts.high + riskCounts.medium + riskCounts.low
                  : -1;
                if (riskCounts && totalRiskCount === 0) {
                  return (
                    <Text className="text-[10px] font-semibold" style={{ color: colors.success }}>
                      No risk
                    </Text>
                  );
                }
                if (riskCounts && totalRiskCount > 0) {
                  return (
                    <View className="flex-row items-center">
                      {riskCounts.high > 0 && (
                        <Text className="text-[10px] font-semibold" style={{ color: colors.error }}>
                          {riskCounts.high} high
                        </Text>
                      )}
                      {riskCounts.high > 0 && riskCounts.medium > 0 && (
                        <Text className="text-[10px] mx-1" style={{ color: colors.text.tertiary }}>
                          ·
                        </Text>
                      )}
                      {riskCounts.medium > 0 && (
                        <Text className="text-[10px] font-semibold" style={{ color: colors.warning }}>
                          {riskCounts.medium} med
                        </Text>
                      )}
                      {(riskCounts.high > 0 || riskCounts.medium > 0) && riskCounts.low > 0 && (
                        <Text className="text-[10px] mx-1" style={{ color: colors.text.tertiary }}>
                          ·
                        </Text>
                      )}
                      {riskCounts.low > 0 && (
                        <Text className="text-[10px] font-semibold" style={{ color: colors.success }}>
                          {riskCounts.low} low
                        </Text>
                      )}
                    </View>
                  );
                }
                return (
                  <Text className="text-[10px] font-semibold" style={{ color: riskColor }}>
                    {riskDisplay}
                  </Text>
                );
              })()}
            </View>
          </View>
        </Pressable>
      </View>

      {/* ── Expanded Content ── */}
      {isOpen && (
        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: colors.border.light,
            paddingHorizontal: 14,
            paddingTop: 12,
            paddingBottom: 14,
          }}
        >
          {/* 1. Full feedback */}
          <Text
            className="text-sm leading-5"
            style={{ color: colors.text.secondary }}
          >
            {feedback}
          </Text>
          {formatClaimsSummary(claimsSummary) ? (
            <Text className="text-xs mt-2" style={{ color: colors.text.tertiary }} numberOfLines={1}>
              {formatClaimsSummary(claimsSummary)}
            </Text>
          ) : null}

          {/* 1b. Sub-criteria audit trail (v2.1.0+) */}
          {subScores && subScores.length > 0 ? (
            <SubCriteriaPanel subScores={subScores} colors={colors} />
          ) : null}

          {/* 2. Evidence detail box */}
          {evidenceDetail ? (
            <View
              className="rounded-lg px-3 py-2 mt-3"
              style={{ backgroundColor: colors.surface.tertiary }}
            >
              <Text className="text-xs leading-4" style={{ color: colors.text.secondary }}>
                {evidenceDetail}
              </Text>
            </View>
          ) : null}

          {/* 3. Risk card */}
          {dimensionRisk ? <RiskCard dimensionRisk={dimensionRisk} colors={colors} /> : null}

          {/* 5. Role-specific */}
          {role === 'startup' && improvementTip ? (
            <ImprovementTipCard tip={improvementTip} colors={colors} />
          ) : null}

          {role === 'investor' ? (
            <>
              {(verificationClaims !== undefined ||
                verificationVerified !== undefined ||
                verificationSelfReported !== undefined ||
                verificationContradictions !== undefined) ? (
                <VerificationTable
                  claims={verificationClaims}
                  verified={verificationVerified}
                  selfReported={verificationSelfReported}
                  contradictions={verificationContradictions}
                  colors={colors}
                />
              ) : null}
              {hasDDFlag ? <DDFlagCard text={ddFlagText} colors={colors} /> : null}
            </>
          ) : null}
        </View>
      )}
    </View>
  );
}

export default DimensionCard;
